import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  Bell,
  CalendarDays,
  ClipboardList,
  FileBarChart,
  Image,
  LayoutGrid,
  LogOut,
  type LucideIcon,
  Menu,
  MessageSquare,
  MessageCircleQuestion,
  Moon,
  Scale,
  Settings,
  Sun,
  Users,
  Wallet,
  X,
  Utensils,
  Leaf,
  ChevronLeft,
  ChevronRight,
  History,
  UsersRound,
} from 'lucide-react';
import { UserRole, adminPath } from '@forever/shared';
import { Avatar, IconButton } from '@/components/ui';
import { CommandPalette } from '@/components/CommandPalette';
import { useAuth } from '@/lib/auth';
import { useDashboard } from '@/lib/queries';
import { useLiveStaffUpdates } from '@/lib/liveUpdates';
import { useThemePreference } from '@/lib/theme';
import './shell.css';

type NavItem = {
  to: string;
  label: string;
  icon: LucideIcon;
  /** Sadece yöneticiye görünen bölümler. */
  adminOnly?: boolean;
  /** Kenar çubuğunda gösterilecek bekleyen iş sayısı. */
  countKey?: 'pendingQuestions' | 'photosAwaitingReview' | 'expiringPackages';
  end?: boolean;
  group?: 'takip' | 'yonetim';
  accent?: string;
};

const NAV: NavItem[] = [
  { to: adminPath.dashboard, label: 'Genel bakış', icon: LayoutGrid, end: true },
  { to: adminPath.clients, label: 'Danışanlar', icon: Users, accent: '125 211 252' },
  { to: adminPath.plans, label: 'Diyet planları', icon: ClipboardList, accent: '94 234 212' },
  { to: adminPath.templates, label: 'Şablonlar', icon: ClipboardList, accent: '196 181 253' },
  { to: adminPath.exchangeLibrary, label: 'Değişim listesi', icon: Utensils, accent: '252 211 77' },
  {
    to: adminPath.messages,
    label: 'Mesajlar',
    icon: MessageSquare,
    group: 'takip',
    accent: '125 211 252',
  },
  {
    to: adminPath.questions,
    label: 'Bekleyen sorular',
    icon: MessageCircleQuestion,
    countKey: 'pendingQuestions',
    group: 'takip',
    accent: '252 211 77',
  },
  {
    to: adminPath.photos,
    label: 'Öğün fotoğrafları',
    icon: Image,
    countKey: 'photosAwaitingReview',
    group: 'takip',
    accent: '253 164 175',
  },
  {
    to: adminPath.measurements,
    label: 'Ölçümler',
    icon: Scale,
    group: 'takip',
    accent: '94 234 212',
  },
  {
    to: adminPath.calendar,
    label: 'Takvim',
    icon: CalendarDays,
    group: 'takip',
    accent: '196 181 253',
  },
  {
    to: adminPath.packages,
    label: 'Paket ve ödeme',
    icon: Wallet,
    adminOnly: true,
    group: 'yonetim',
    accent: '252 211 77',
  },
  {
    to: adminPath.reports,
    label: 'Raporlar',
    icon: FileBarChart,
    group: 'yonetim',
    accent: '165 180 252',
  },
  {
    to: adminPath.team,
    label: 'Ekip',
    icon: UsersRound,
    adminOnly: true,
    group: 'yonetim',
    accent: '94 234 212',
  },
  {
    to: adminPath.notificationRules,
    label: 'Bildirimler',
    icon: Bell,
    adminOnly: true,
    group: 'yonetim',
  },
  {
    to: adminPath.audit,
    label: 'İşlem geçmişi',
    icon: History,
    adminOnly: true,
    group: 'yonetim',
    accent: '148 163 184',
  },
  {
    to: adminPath.settings,
    label: 'Ayarlar',
    icon: Settings,
    adminOnly: true,
    group: 'yonetim',
    accent: '148 163 184',
  },
];

export function Shell({ children }: { children: ReactNode }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem('forever.admin.sidebar') === 'collapsed';
    } catch {
      return false;
    }
  });
  const [mobile, setMobile] = useState(() => window.matchMedia('(max-width: 1024px)').matches);
  const location = useLocation();
  useLiveStaffUpdates();

  // Sayfa değişince gezinme çekmecesi kapanır.
  useEffect(() => setMenuOpen(false), [location.pathname]);
  useEffect(() => {
    const media = window.matchMedia('(max-width: 1024px)');
    const update = () => {
      setMobile(media.matches);
      setMenuOpen(false);
    };
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);

  const toggleSidebar = () =>
    setCollapsed((value) => {
      try {
        localStorage.setItem('forever.admin.sidebar', value ? 'expanded' : 'collapsed');
      } catch {
        /* The sidebar still works without browser storage. */
      }
      return !value;
    });

  return (
    <div className="fd-shell" data-collapsed={collapsed ? 'true' : undefined}>
      {mobile && menuOpen ? (
        <button
          className="fd-sidebar-backdrop"
          aria-label="Menüyü kapat"
          tabIndex={-1}
          onClick={() => setMenuOpen(false)}
        />
      ) : null}
      <Sidebar
        open={menuOpen}
        mobile={mobile}
        collapsed={collapsed}
        onToggle={toggleSidebar}
        onClose={() => setMenuOpen(false)}
      />
      <div className="fd-main" inert={mobile && menuOpen ? true : undefined}>
        <Topbar menuOpen={menuOpen} onMenu={() => setMenuOpen(true)} />
        <main className="fd-page">{children}</main>
      </div>
    </div>
  );
}

function Sidebar({
  open,
  mobile,
  collapsed,
  onToggle,
  onClose,
}: {
  open: boolean;
  mobile: boolean;
  collapsed: boolean;
  onToggle: () => void;
  onClose: () => void;
}) {
  const panel = useRef<HTMLElement>(null);
  const user = useAuth((s) => s.user);
  const { data: dashboard } = useDashboard();
  const isAdmin = user?.role === UserRole.ADMIN;

  useEffect(() => {
    if (!mobile || !open) return;
    const previousFocus = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    panel.current?.querySelector<HTMLElement>('button, a')?.focus();
    return () => {
      document.body.style.overflow = previousOverflow;
      previousFocus?.focus();
    };
  }, [mobile, open]);

  const counts = {
    pendingQuestions: dashboard?.metrics.pendingQuestions ?? 0,
    photosAwaitingReview: dashboard?.metrics.photosAwaitingReview ?? 0,
    expiringPackages: dashboard?.metrics.expiringPackages ?? 0,
  };

  return (
    <aside
      ref={panel}
      id="fd-sidebar"
      className="fd-sidebar"
      data-open={open ? 'true' : undefined}
      aria-label="Ana menü"
      role={mobile && open ? 'dialog' : undefined}
      aria-modal={mobile && open ? true : undefined}
      inert={mobile && !open ? true : undefined}
      onKeyDown={(event) => {
        if (!mobile || !open) return;
        if (event.key === 'Escape') {
          event.preventDefault();
          onClose();
        }
        if (event.key === 'Tab') {
          const items = Array.from(
            panel.current?.querySelectorAll<HTMLElement>('a[href], button:not([disabled])') ?? [],
          ).filter((item) => item.getClientRects().length > 0);
          const first = items[0];
          const last = items[items.length - 1];
          if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last?.focus();
          } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first?.focus();
          }
        }
      }}
    >
      <div className="fd-sidebar__brand">
        <NavLink
          to={adminPath.dashboard}
          className="fd-brand"
          aria-label="Forever Deluxe — Genel bakış"
        >
          <span className="fd-brand__mark">
            <Leaf size={23} aria-hidden />
          </span>
          <span className="fd-brand__text">
            <strong>Forever Deluxe</strong>
            <small>DANIŞAN YÖNETİMİ</small>
          </span>
        </NavLink>
        <span className="fd-hide-desktop">
          <IconButton icon={X} label="Menüyü kapat" onClick={onClose} />
        </span>
      </div>

      <nav className="fd-nav" aria-label="Ana gezinme">
        {(
          [
            { key: undefined, label: 'Çalışma alanı' },
            { key: 'takip', label: 'İletişim ve takip' },
            { key: 'yonetim', label: 'Yönetim' },
          ] as const
        ).map((group) => (
          <div className="fd-nav__group" key={group.label}>
            <p className="fd-nav__heading">{group.label}</p>
            {NAV.filter((item) => item.group === group.key && (!item.adminOnly || isAdmin)).map(
              (item) => {
                const count = item.countKey ? counts[item.countKey] : 0;
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.end}
                    className="fd-nav__item"
                    title={collapsed && !mobile ? item.label : undefined}
                    style={{ '--nav-accent': item.accent ?? '211 255 171' } as CSSProperties}
                  >
                    <span className="fd-nav__icon">
                      <item.icon size={17} aria-hidden />
                    </span>
                    <span className="fd-nav__label">{item.label}</span>
                    {count > 0 ? <span className="fd-nav__count">{count}</span> : null}
                  </NavLink>
                );
              },
            )}
          </div>
        ))}
      </nav>

      <div className="fd-sidebar__footer">
        <span className="fd-sidebar__status" aria-hidden />
        <span className="fd-sidebar__caption">Daha sağlıklı yarınlara</span>
        <button
          type="button"
          className="fd-sidebar__collapse"
          aria-label={collapsed ? 'Menüyü genişlet' : 'Menüyü daralt'}
          aria-expanded={!collapsed}
          aria-controls="fd-sidebar"
          onClick={onToggle}
        >
          {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
        </button>
      </div>
    </aside>
  );
}

function Topbar({ menuOpen, onMenu }: { menuOpen: boolean; onMenu: () => void }) {
  const user = useAuth((s) => s.user);
  const signOut = useAuth((s) => s.signOut);
  const navigate = useNavigate();
  const { mode, toggle } = useThemePreference();
  const location = useLocation();
  const currentPage =
    NAV.find(
      (item) =>
        item.to === location.pathname ||
        (item.to !== '/' && location.pathname.startsWith(`${item.to}/`)),
    )?.label ?? 'Çalışma alanı';

  const roleLabel =
    user?.role === UserRole.ADMIN
      ? 'Yönetici'
      : user?.role === UserRole.DIETITIAN
        ? 'Diyetisyen'
        : '';

  return (
    <header className="fd-topbar">
      <span className="fd-hide-desktop">
        <IconButton
          icon={Menu}
          label="Menüyü aç"
          aria-expanded={menuOpen}
          aria-controls="fd-sidebar"
          onClick={onMenu}
        />
      </span>
      <div className="fd-topbar__location">
        <span>Çalışma alanı</span>
        <ChevronRight size={12} aria-hidden />
        <strong>{currentPage}</strong>
      </div>
      <div className="fd-spacer" />
      <CommandPalette />

      <IconButton
        icon={mode === 'dark' ? Sun : Moon}
        label={mode === 'dark' ? 'Açık temaya geç' : 'Koyu temaya geç'}
        onClick={toggle}
      />

      <div className="fd-topbar__profile fd-row" style={{ gap: 'var(--fd-space-2)' }}>
        <Avatar name={user?.fullName ?? ''} src={user?.avatarUrl} size={32} />
        <div className="fd-topbar__identity" style={{ lineHeight: 1.25 }}>
          <div style={{ fontSize: 'var(--fd-text-sm)', fontWeight: 600 }}>{user?.fullName}</div>
          <div style={{ fontSize: 'var(--fd-text-xs)', color: 'var(--fd-color-text-muted)' }}>
            {roleLabel}
          </div>
        </div>
      </div>

      <IconButton
        icon={LogOut}
        label="Çıkış yap"
        onClick={async () => {
          await signOut();
          navigate('/giris', { replace: true });
        }}
      />
    </header>
  );
}

/** Sayfa başlığı bloğu — her sayfanın üstünde aynı ritim. */
export function PageHeader({
  title,
  description,
  breadcrumb,
  actions,
}: {
  title: string;
  description?: string;
  breadcrumb?: string;
  actions?: ReactNode;
}) {
  return (
    <div
      className="fd-page-header fd-row fd-row--between fd-row--wrap"
      style={{ gap: 'var(--fd-space-4)' }}
    >
      <div style={{ minWidth: 0 }}>
        {breadcrumb ? (
          <p style={{ fontSize: 'var(--fd-text-xs)', color: 'var(--fd-color-text-subtle)' }}>
            {breadcrumb}
          </p>
        ) : null}
        <h1 className="fd-display" style={{ fontSize: 'var(--fd-text-3xl)' }}>
          {title}
        </h1>
        {description ? (
          <p style={{ color: 'var(--fd-color-text-muted)', marginTop: 4, maxWidth: 680 }}>
            {description}
          </p>
        ) : null}
      </div>
      {actions ? (
        <div className="fd-page-header__actions fd-row" style={{ gap: 'var(--fd-space-2)' }}>
          {actions}
        </div>
      ) : null}
    </div>
  );
}
